import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from './api';
import { TAG_KINDS, TagChip } from './tags';
import { TagManager } from './TagReport';
import { playForTrade } from './Playbook';
import { GradePicker } from './grades';
import { rOf, rText, riskOf } from './rMultiple';
import { useI18n } from './i18n';
import { TradeScreenshots } from './Screenshots';
import TradeChart from './TradeChart';
import { trackEvent } from './analytics';
import { formatDuration, formatMoney, grossPnl, holdMs, netPnl, usd } from './pnl';
import { currentLocale } from './langState';
import { BehaviorNote } from './BehaviorInsights';

// The trade at a glance, above the parts you can edit.
function TradeSummary({ trade, accounts }) {
  const { t } = useI18n();
  const net = netPnl(trade);
  const gross = grossPnl(trade);
  const size = trade.size || 1;
  const points = (trade.side === 'Short' ? trade.entryPrice - trade.exitPrice : trade.exitPrice - trade.entryPrice);
  const held = holdMs(trade);
  const account = (accounts || []).find((a) => a._id === trade.accountId);
  const time = (d) => new Date(d).toLocaleTimeString(currentLocale(), { hour: 'numeric', minute: '2-digit' });
  const tone = (n) => (n > 0 ? 'gain' : n < 0 ? 'loss' : '');
  const facts = [
    [t('Gross P&L'), formatMoney(gross), tone(gross)],
    [t('Fees'), trade.fees ? usd.format(trade.fees) : '—'],
    [t('Points'), `${points > 0 ? '+' : ''}${points.toFixed(2)}${size > 1 ? ` × ${size}` : ''}`, tone(points)],
    [t('Entry → exit'), `${trade.entryPrice.toFixed(2)} → ${trade.exitPrice.toFixed(2)}`],
    [t('Time'), trade.timeUnknown ? '—' : trade.exitDate ? `${time(trade.date)} → ${time(trade.exitDate)}` : time(trade.date)],
    [t('Held'), held === null ? '—' : formatDuration(held)],
    ...(riskOf(trade) ? [[t('Result in R'), `${rText(rOf(trade))} · ${t(riskOf(trade).from === 'usual' ? 'your usual {amount} risk' : '{amount} at risk', { amount: usd.format(riskOf(trade).risk) })}`, tone(rOf(trade))]] : []),
    ...(account ? [[t('Account'), account.name]] : []),
  ];
  return (
    <div className="trade-summary">
      <div className="trade-summary-net">
        <span className="modal-section-label">{t('Net P&L')}</span>
        <strong className={tone(net)}>{formatMoney(net)}</strong>
      </div>
      <dl className="trade-summary-facts">
        {facts.map(([label, value, cls]) => (
          <div key={label}>
            <dt>{label}</dt>
            <dd className={cls || undefined}>{value}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

function toLocalInput(date) {
  const d = new Date(date);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function formFromTrade(t) {
  return {
    accountId: t.accountId || '',
    symbol: t.symbol,
    side: t.side || 'Long',
    size: String(t.size || 1),
    entryPrice: String(t.entryPrice),
    exitPrice: String(t.exitPrice),
    pnl: typeof t.pnl === 'number' ? String(t.pnl) : '',
    fees: t.fees ? String(t.fees) : '',
    date: toLocalInput(t.date),
    notes: t.notes || '',
    tags: t.tags || [],
    rulesMet: t.rulesMet || [],
    grade: t.grade || '',
    stopPrice: Number.isFinite(t.stopPrice) ? String(t.stopPrice) : '',
    risk: Number(t.risk) > 0 ? String(t.risk) : '',
  };
}

// Tag selection plus a box for creating new tags, which are added to the library.
export function TagPicker({ selected, library, onToggle, onCreate }) {
  const { t } = useI18n();
  const [newName, setNewName] = useState('');
  const [newKind, setNewKind] = useState('mistake');
  const extra = selected.filter((name) => !library.some((x) => x.name.toLowerCase() === name.toLowerCase()));

  function create() {
    const name = newName.trim().replace(/\s+/g, ' ');
    if (!name) return;
    onCreate({ name, kind: newKind });
    setNewName('');
  }

  return (
    <div className="tag-picker">
      {TAG_KINDS.map(({ kind, label }) => {
        const tags = library.filter((x) => x.kind === kind);
        const extras = kind === 'context' ? extra : [];
        if (!tags.length && !extras.length) return null;
        return (
          <div key={kind} className="tag-group">
            <span className="tag-group-label">{t(label)}</span>
            <div className="tag-list">
              {tags.map((x) => (
                <TagChip key={x.name} name={x.name} kind={kind} selected={selected.includes(x.name)}
                  onClick={() => onToggle(x.name)} />
              ))}
              {extras.map((name) => (
                <TagChip key={name} name={name} kind="context" selected onClick={() => onToggle(name)} />
              ))}
            </div>
          </div>
        );
      })}
      <div className="tag-create">
        <input
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              create();
            }
          }}
          placeholder={t('New tag, e.g. Overtrading')}
          maxLength={40}
          aria-label={t('New tag name')}
        />
        <select value={newKind} onChange={(e) => setNewKind(e.target.value)} aria-label={t('New tag kind')}>
          <option value="setup">{t('Setup')}</option>
          <option value="mistake">{t('Mistake')}</option>
          <option value="context">{t('Context')}</option>
        </select>
        <button type="button" className="secondary small" onClick={create} disabled={!newName.trim()}>
          {t('Add tag')}
        </button>
      </div>
    </div>
  );
}

function TradeEditor({ trade, accounts, library, plays = [], behaviorFlags, onLibraryChange, onSaved, onUpdated, onDeleted, onClose, onSessionExpired }) {
  useEffect(() => trackEvent('Trade window'), []);
  const { t } = useI18n();
  const [form, setForm] = useState(() => formFromTrade(trade));
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [managingTags, setManagingTags] = useState(false);
  const dialog = useRef(null);
  const hasBrokerPnl = typeof trade.pnl === 'number';
  const play = playForTrade({ tags: form.tags }, plays);
  const playRules = play ? play.entryRules : [];

  function toggleRule(rule) {
    setForm((f) => ({ ...f, rulesMet: f.rulesMet.includes(rule) ? f.rulesMet.filter((r) => r !== rule) : [...f.rulesMet, rule] }));
  }

  useEffect(() => {
    dialog.current?.focus();
    function onKey(e) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  function handleChange(e) {
    setForm({ ...form, [e.target.name]: e.target.value });
  }

  function toggleTag(name) {
    setForm((f) => ({
      ...f,
      tags: f.tags.includes(name) ? f.tags.filter((t) => t !== name) : [...f.tags, name],
    }));
  }

  async function createTag(tag) {
    const existing = library.find((t) => t.name.toLowerCase() === tag.name.toLowerCase());
    const name = existing ? existing.name : tag.name;
    if (!existing) {
      try {
        const saved = await api('/tags', { method: 'PUT', body: [...library, tag] });
        onLibraryChange(saved);
      } catch (err) {
        if (err.status === 401) return onSessionExpired();
        setError(err.message);
        return;
      }
    }
    setForm((f) => (f.tags.includes(name) ? f : { ...f, tags: [...f.tags, name] }));
  }

  // Adds the trade to a Playbook play (or to none): a trade belongs to a play through the play's
  // setup tag, so this swaps the trade's play tag. The tag is added to the tag library if missing.
  async function choosePlay(next) {
    const playTags = plays.map((p) => p.tag.toLowerCase());
    setForm((f) => ({ ...f, tags: f.tags.filter((x) => !playTags.includes(x.toLowerCase())), ...(next ? {} : { rulesMet: [] }) }));
    if (next) await createTag({ name: next.tag, kind: 'setup' });
  }

  function handleApiError(err) {
    if (err.status === 401) return onSessionExpired();
    setError(err.message);
  }

  async function handleSave(e) {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      const body = {
        ...(form.accountId ? { accountId: form.accountId } : {}),
        symbol: form.symbol.trim().toUpperCase(),
        side: form.side,
        size: Number(form.size),
        entryPrice: Number(form.entryPrice),
        exitPrice: Number(form.exitPrice),
        pnl: form.pnl === '' ? null : Number(form.pnl),
        fees: form.fees === '' ? 0 : Math.abs(Number(form.fees)),
        date: new Date(form.date).toISOString(),
        notes: form.notes.trim(),
        tags: form.tags,
        grade: form.grade || null,
        stopPrice: form.stopPrice === '' ? null : Number(form.stopPrice),
        risk: form.risk === '' ? null : Math.abs(Number(form.risk)),
        // Saving with the play's rules on screen counts as reviewing them.
        ...(playRules.length ? { rulesMet: form.rulesMet.filter((r) => playRules.includes(r)), rulesReviewed: true } : {}),
      };
      const saved = await api(`/trades/${trade._id}`, { method: 'PATCH', body });
      onSaved(saved);
    } catch (err) {
      handleApiError(err);
      setSaving(false);
    }
  }

  async function handleDelete() {
    setError('');
    setSaving(true);
    try {
      await api(`/trades/${trade._id}`, { method: 'DELETE' });
      onDeleted(trade._id);
    } catch (err) {
      handleApiError(err);
      setSaving(false);
    }
  }

  return (
    <div className="modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <form ref={dialog} tabIndex={-1} className="modal" onSubmit={handleSave} role="dialog" aria-modal="true"
        aria-labelledby="edit-trade-title">
        <div className="modal-header">
          <div>
            <h2 id="edit-trade-title">{trade.symbol} · {t(trade.side || 'Long')} &times; {trade.size || 1}</h2>
            <p className="manual-trade-day">
              {new Date(trade.date).toLocaleDateString(currentLocale(), { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
            </p>
          </div>
          <button type="button" className="modal-close" onClick={onClose} aria-label={t('Close')}>&times;</button>
        </div>

        <TradeSummary trade={trade} accounts={accounts} />

        <BehaviorNote flags={behaviorFlags} />

        <TradeChart trade={trade} />

        {onUpdated && <TradeScreenshots trade={trade} onTradeUpdated={onUpdated} onSessionExpired={onSessionExpired} />}

        <div className="modal-section">
          <span className="modal-section-label">{t('Initial risk')}</span>
          <div className="risk-fields">
            <label>{t('First stop price')}<input name="stopPrice" type="number" step="any" value={form.stopPrice} onChange={handleChange} placeholder={t('e.g. {price}', { price: trade.entryPrice })} /></label>
            <label>{t('Or dollars at risk')}<input name="risk" type="number" step="any" min="0" value={form.risk} onChange={handleChange} placeholder="$" /></label>
          </div>
          {(() => {
            const draft = { ...trade, stopPrice: form.stopPrice === '' ? undefined : Number(form.stopPrice), risk: form.risk === '' ? undefined : Number(form.risk) };
            const r = riskOf(draft);
            return <span className="field-hint">{r ? t('Risk {amount} ({from}): this trade made {r}.', { amount: usd.format(r.risk), from: t(r.from === 'stop' ? 'from the stop' : r.from === 'risk' ? 'typed in' : 'your usual risk'), r: rText(rOf(draft)) }) : t('Where your stop was when you entered. It turns the result into R: +2R made twice what you risked.')}</span>;
          })()}
        </div>

        <div className="modal-section">
          <span className="modal-section-label">{t('Grade')}</span>
          <GradePicker value={form.grade} onChange={(grade) => setForm((f) => ({ ...f, grade }))} />
          <span className="field-hint">{t('Grade how well you executed your plan, not whether it made money.')}</span>
        </div>

        <div className="modal-section">
          <div className="modal-section-head">
            <span className="modal-section-label">{t('Tags')}</span>
            <button type="button" className="link-button" onClick={() => setManagingTags(!managingTags)}>
              {managingTags ? t('Done managing tags') : t('Manage tags')}
            </button>
          </div>
          {managingTags ? (
            <TagManager library={library} onLibraryChange={onLibraryChange} onSessionExpired={onSessionExpired} inTradeWindow />
          ) : (
            <TagPicker selected={form.tags} library={library} onToggle={toggleTag} onCreate={createTag} />
          )}
        </div>

        <div className="modal-section play-pick">
          <div className="modal-section-head">
            <span className="modal-section-label">{t('Playbook')}</span>
            <Link to="/app/playbook" className="link-button" onClick={onClose}>{plays.length ? t('Manage plays') : t('Create a play')}</Link>
          </div>
          {plays.length ? (
            <>
              <div className="play-options" role="radiogroup" aria-label={t('Which play was this trade?')}>
                <button type="button" role="radio" aria-checked={!play} className={`secondary small${!play ? ' active' : ''}`} onClick={() => choosePlay(null)}>{t('No play')}</button>
                {plays.filter((p) => p.status !== 'retired' || (play && play._id === p._id)).map((p) => (
                  <button key={p._id} type="button" role="radio" aria-checked={Boolean(play && play._id === p._id)}
                    className={`secondary small${play && play._id === p._id ? ' active' : ''}`} onClick={() => choosePlay(p)}>
                    {p.name}{p.status === 'testing' && <em> · {t('testing')}</em>}
                  </button>
                ))}
              </div>
              <span className="field-hint">{t('Which play was this trade? It counts toward that play’s results in your Playbook.')}</span>
            </>
          ) : (
            <span className="field-hint">{t('Plays are the setups you trade on purpose, with their own rules. Create one in your Playbook to add trades to it.')}</span>
          )}
        </div>

        {playRules.length > 0 && (
          <div className="modal-section play-check">
            <span className="modal-section-label">{t('Playbook: {name}', { name: play.name })}</span>
            <span className="field-hint">{t('Which entry rules did this trade follow?')}</span>
            <ul className="checklist">
              {playRules.map((rule) => {
                const checked = form.rulesMet.includes(rule);
                return (
                  <li key={rule}>
                    <label className={`check-item${checked ? ' checked' : ''}`}>
                      <input type="checkbox" checked={checked} onChange={() => toggleRule(rule)} />
                      <span>{rule}</span>
                    </label>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        <label>
          {t('Notes')}
          <textarea name="notes" rows="3" value={form.notes} onChange={handleChange}
            placeholder={t('What happened on this trade?')} maxLength={5000} />
        </label>

        <details className="modal-details">
          <summary>{t('Trade details')}</summary>
          <div className="fields">
            {accounts.length > 0 && (
              <label>
                {t('Account')}
                <select name="accountId" value={form.accountId} onChange={handleChange}>
                  {accounts.filter((a) => !a.archived || a._id === form.accountId).map((a) => (
                    <option key={a._id} value={a._id}>{a.name}{a.archived ? ` ${t('(archived)')}` : ''}</option>
                  ))}
                </select>
              </label>
            )}
            <label>
              {t('Symbol')}
              <input name="symbol" value={form.symbol} onChange={handleChange} required maxLength={20} />
            </label>
            <label>
              {t('Side')}
              <select name="side" value={form.side} onChange={handleChange}>
                <option value="Long">{t('Long')}</option>
                <option value="Short">{t('Short')}</option>
              </select>
            </label>
            <label>
              {t('Size')}
              <input name="size" type="number" step="any" min="0" value={form.size} onChange={handleChange} required />
            </label>
            <label>
              {t('Entry price')}
              <input name="entryPrice" type="number" step="any" value={form.entryPrice} onChange={handleChange} required />
            </label>
            <label>
              {t('Exit price')}
              <input name="exitPrice" type="number" step="any" value={form.exitPrice} onChange={handleChange} required />
            </label>
            <label>
              {t('Date & time')}
              <input name="date" type="datetime-local" value={form.date} onChange={handleChange} required />
            </label>
            <label>
              {t('P&L before fees ($)')}
              <input name="pnl" type="number" step="any" value={form.pnl} onChange={handleChange} placeholder={t('Auto')} />
            </label>
            <label>
              {t('Fees ($)')}
              <input name="fees" type="number" step="any" min="0" value={form.fees} onChange={handleChange} placeholder="0" />
            </label>
          </div>
          <p className="field-hint">
            {hasBrokerPnl
              ? t("P&L came from your broker's file. Clear it to have it worked out from the prices, side and size instead.")
              : t('Leave P&L empty to work it out from the prices, side and size.')}
          </p>
        </details>

        {error && <p className="error">{error}</p>}

        <div className="modal-actions">
          <button type="submit" disabled={saving}>{saving ? t('Saving…') : t('Save changes')}</button>
          <button type="button" className="secondary" onClick={onClose} disabled={saving}>{t('Cancel')}</button>
          <div className="modal-delete">
            {confirmDelete ? (
              <>
                <span>{t('Delete this trade?')}</span>
                <button type="button" className="danger small" onClick={handleDelete} disabled={saving}>
                  {t('Yes, delete')}
                </button>
                <button type="button" className="link-button" onClick={() => setConfirmDelete(false)}>{t('No')}</button>
              </>
            ) : (
              <button type="button" className="link-button danger-link" onClick={() => setConfirmDelete(true)}>
                {t('Delete trade')}
              </button>
            )}
          </div>
        </div>
      </form>
    </div>
  );
}

export default TradeEditor;
